import CircularProgress from 'components/shared/CircularProgress';
import Workflow from 'models/Workflow';
import { WorkflowActionIntent, WorkflowValuesInput } from 'services/api/workflows';
import DeactivatedUserIcon from 'svg/DeactivatedUserIcon';
import ErrorOccurredIcon from 'svg/ErrorOccurredIcon';
import SSOErrorIcon from 'svg/SSOErrorIcon';

import { VALUE_NAMES } from '../constants';
import DetailSlideout from '../DetailSlideout';

import { PAYER_ID_VALUE, PAYER_LOGO_VALUE, readEligibility } from './eligibilityData';
import EligibilityDetailsHeader from './EligibilityDetailsHeader';
import EligibilityEmptyState from './EligibilityEmptyState';
import EligibilityResultView from './EligibilityResultView';
import SelectPayerView from './SelectPayerView';

// The Insurance Eligibility Check slideout. Opened from the Payer Search / Insurance Verification
// rows; derives which of the six states to show from the two auto-check actions and renders the
// matching subview. Interactions run through the same complete/invoke handlers the card uses:
// picking a payer completes PayerSearch with its payer_id; Try Again re-runs BenefitsEligibility.

interface Props {
  open: boolean;
  onClose: () => void;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
  onEditDetails: () => void;
  // The coverage being verified (design 008 §3e); null on a referral with no coverages. Every
  // value this slideout renders belongs to one coverage, so an unscoped read would show a
  // sibling coverage's payer next to this one's results.
  facetId: string | null;
}

export default function EligibilityCheckSlideout({
  open,
  onClose,
  workflow,
  onComplete,
  isCompleting,
  onInvoke,
  isInvoking,
  onEditDetails,
  facetId,
}: Props) {
  const { payerSearch, benefits, candidates, result, state } = readEligibility(workflow, facetId);
  // plan_type stays a REFERRAL-level value — it feeds the referral list's Plan Type column and
  // OLIO-2793 deliberately leaves it alone. Read it unscoped so that stays obvious; the
  // coverage's own payer_name is what the tabs will show (OLIO-2794).
  const payerName = workflow.valueFor(VALUE_NAMES.PLAN_TYPE)?.value;
  // Committed at pick time (PayerSearch's candidates are gone by result time — see readEligibility).
  const payerLogo = (workflow.valueFor(PAYER_LOGO_VALUE, { facetId })?.value as string) || undefined;

  const runCheck = (payerId: string) => {
    if (!payerSearch) return;
    // Carry the selected payer's logo forward so the result Overview can show it.
    const logo = candidates.find((c) => c.primaryPayerId === payerId)?.logo ?? '';
    onComplete(payerSearch.id, { [PAYER_ID_VALUE]: payerId, [PAYER_LOGO_VALUE]: logo });
  };
  const retry = () => {
    if (benefits) onInvoke(benefits.id, 'rerun');
  };

  return (
    <DetailSlideout
      open={open}
      onClose={onClose}
      title='Benefit Verification'
      subtitle='Verifying patient has active insurance coverage and eligibility'>
      <EligibilityDetailsHeader workflow={workflow} onEditDetails={onEditDetails} facetId={facetId} />

      {state === 'select-payer' && (
        <SelectPayerView candidates={candidates} searchTerm={payerName} onRun={runCheck} isRunning={isCompleting} />
      )}

      {state === 'active-coverage' && result && (
        <EligibilityResultView result={result} active workflowId={workflow.id} payerLogo={payerLogo} />
      )}
      {state === 'no-active-coverage' && result && (
        <EligibilityResultView result={result} active={false} workflowId={workflow.id} payerLogo={payerLogo} />
      )}

      {state === 'payer-not-enrolled' && (
        <EligibilityEmptyState
          icon={<SSOErrorIcon width={56} height={56} />}
          title='Payer Not Enrolled in Eligibility Check'
          body={
            <>
              Unfortunately, this patient’s payer is not enrolled in Olio’s automated eligibility check.
              <br />
              Reach out to the payer for details around this patient’s eligibility.
            </>
          }
        />
      )}

      {state === 'no-results' && (
        <EligibilityEmptyState
          icon={<DeactivatedUserIcon width={56} height={56} />}
          title='No Results Found'
          body={
            <>
              This patient couldn’t be found with the provided information.
              <br />
              <strong>
                Please verify the member ID, name, and date of birth match the insurance card, then try again.
              </strong>
            </>
          }
          onTryAgain={retry}
          isRetrying={isInvoking}
        />
      )}

      {state === 'error' && (
        <EligibilityEmptyState
          icon={<ErrorOccurredIcon width={56} height={56} />}
          title='Something Went Wrong'
          body={
            <>
              Something went wrong while checking eligibility.
              <br />
              Please try again or contact support if the issue persists.
            </>
          }
          onTryAgain={retry}
          isRetrying={isInvoking}
        />
      )}

      {/* Reachable only if the slideout was open when the run got canceled (poll flips the
          state under it) — the row-level entry links are suppressed for canceled. */}
      {state === 'canceled' && (
        <EligibilityEmptyState
          icon={<DeactivatedUserIcon width={56} height={56} />}
          title='Eligibility Check Canceled'
          body='This referral was canceled before the eligibility check finished.'
        />
      )}

      {(state === 'running' || state === 'pending') && (
        <EligibilityEmptyState
          icon={<CircularProgress />}
          title={state === 'running' ? 'Running eligibility check…' : 'Eligibility check not started'}
          body={
            state === 'running'
              ? 'This can take a few moments. The results will appear here when the check completes.'
              : 'This check hasn’t run yet. It will start once the required details are available.'
          }
        />
      )}
    </DetailSlideout>
  );
}
